// SPDX-License-Identifier: GPL-3.0-or-later
// The stream of the game page. ONE source of truth for the choice: the `streamBackground` field of
// the settings store. The layer shows that source at the start and at each change of the store, but
// only while the Twitch channel is live (live-status.ts asks /api/live). With the channel offline,
// or the choice "off", there is no frame and the page makes no request to a player. The "off" choice
// makes no request to /api/live either.
// The Twitch panel sits beside the table, so the renderer always keeps its opaque backdrop.
// An old saved "youtube" choice loads as the default choice (see `normalizeSource`).
// The kill switch STREAM_BACKGROUND_ENABLED (stream-config.ts) turns the whole layer off.

import type { PinballRenderer } from "../src/render/index.ts";
import type { SettingsStore } from "../src/shared/feedback/index.ts";
import {
  createLiveStatus,
  type LiveFetch,
  type LiveStatusDeps,
  type VisibilitySource,
} from "./live-status.ts";
import { createStreamBackground, type StreamBackgroundDeps } from "./stream-bg.ts";
import { STREAM_BACKGROUND_ENABLED, type StreamSource } from "./stream-config.ts";

/** What the live check takes from the platform. Each part has a default (the browser). */
export interface StreamLiveDeps {
  readonly fetchFn?: LiveFetch | null;
  readonly now?: () => number;
  readonly setTimer?: LiveStatusDeps["setTimer"];
  readonly clearTimer?: LiveStatusDeps["clearTimer"];
  /** The page visibility. Default: the owner document of the host, when it has the events. */
  readonly visibility?: VisibilitySource | null;
}

/** What the layer takes from the page. */
export interface StreamLayerDeps {
  readonly host: StreamBackgroundDeps["host"];
  readonly location: StreamBackgroundDeps["location"];
  readonly win?: StreamBackgroundDeps["win"];
  readonly playfield?: StreamBackgroundDeps["playfield"];
  readonly view: Pick<PinballRenderer, "setBackdropTransparent">;
  readonly settings: Pick<SettingsStore, "get" | "subscribe">;
  /** The kill switch. Default: `STREAM_BACKGROUND_ENABLED`. False: no layer and no request. */
  readonly enabled?: boolean;
  readonly live?: StreamLiveDeps;
}

/** The stream layer. */
export interface StreamLayer {
  /** The source that shows now ("off" while the channel is not live). */
  source(): StreamSource;
  /** Remove the layer and give the renderer its opaque backdrop back. */
  dispose(): void;
}

/** The global fetch, or null where there is none. */
function browserFetch(): LiveFetch | null {
  return typeof fetch === "function" ? (input, init) => fetch(input, init) : null;
}

/** The visibility events of a document, or null when the document has none (a test double). */
function visibilityOf(doc: unknown): VisibilitySource | null {
  const candidate = doc as Partial<VisibilitySource> | null | undefined;
  const usable =
    typeof candidate?.addEventListener === "function" &&
    typeof candidate.removeEventListener === "function";
  return usable ? (candidate as VisibilitySource) : null;
}

/** The pieces of the live check, with the browser as the default. */
function liveParts(deps: StreamLayerDeps): Omit<LiveStatusDeps, "onChange"> {
  const live = deps.live ?? {};
  return {
    fetchFn: live.fetchFn === undefined ? browserFetch() : live.fetchFn,
    now: live.now ?? Date.now,
    setTimer: live.setTimer ?? ((fn, ms) => setTimeout(fn, ms)),
    clearTimer: live.clearTimer ?? ((handle) => clearTimeout(handle as number)),
    visibility:
      live.visibility === undefined ? visibilityOf(deps.host.ownerDocument) : live.visibility,
  };
}

/** The layer of a page where the kill switch is on. */
function disabledLayer(view: StreamLayerDeps["view"]): StreamLayer {
  view.setBackdropTransparent(false);
  return { source: () => "off", dispose: () => undefined };
}

/** Make the stream layer. */
export function createStreamLayer(deps: StreamLayerDeps): StreamLayer {
  const { view, settings } = deps;
  if (!(deps.enabled ?? STREAM_BACKGROUND_ENABLED)) return disabledLayer(view);
  const bg = createStreamBackground({
    host: deps.host,
    location: deps.location,
    win: deps.win ?? null,
    playfield: deps.playfield ?? null,
    initial: settings.get().streamBackground,
  });
  view.setBackdropTransparent(false);
  const status = createLiveStatus({
    ...liveParts(deps),
    onChange: (live) => {
      bg.setLive(live);
    },
  });
  const apply = (): void => {
    const choice = settings.get().streamBackground;
    if (choice === "off") {
      status.stop();
      bg.setLive(false);
    } else {
      status.start();
    }
    bg.setSource(choice);
  };
  apply();
  const stop = settings.subscribe(apply);
  return {
    source: () => bg.getSource(),
    dispose() {
      stop();
      status.stop();
      bg.dispose();
      view.setBackdropTransparent(false);
    },
  };
}
